/**
 * Hook to delete an ABR filter.
 *
 * Follows the same refetch pattern as useDeleteCommunityPolicy.
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { DELETE_ABR_FILTER, GET_ABR_FILTERS } from '@moderation/graphql';
import { useCallback, useMemo, useState } from 'react';

interface UseDeleteAbrFilterResult {
  deleteFilter: (filterId: string) => Promise<boolean>;
  loading: boolean;
  errors: DomainError[] | null;
  resetErrors: () => void;
}

export function useDeleteAbrFilter(communityId: string): UseDeleteAbrFilterResult {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [executeMutation, { loading }] = useMutation(DELETE_ABR_FILTER, {
    refetchQueries: communityId ? [{ query: GET_ABR_FILTERS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
    onError: error => {
      setErrors(transformErrors(error));
    },
  });

  const deleteFilter = useCallback(
    async (filterId: string): Promise<boolean> => {
      setErrors(null);

      const result = await executeMutation({
        variables: { input: { communityId, filterId } },
      });

      return result.data?.deleteAbrFilter ?? false;
    },
    [executeMutation, communityId]
  );

  const resetErrors = useCallback(() => setErrors(null), []);

  return useMemo(() => ({ deleteFilter, loading, errors, resetErrors }), [deleteFilter, loading, errors, resetErrors]);
}
